import { useEffect, useState, useMemo } from "react";
import { usePrefixedNavigate } from "../../hooks/usePrefixedNavigate";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../integrations/supabase/client";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { FileText, MessageSquare, Users, Award, Plus } from "lucide-react";
import StudentLayout from "../../components/StudentLayout";
import { formatDate } from "../../lib/utils";
import {
  DataTable,
  DataTableColumnSettings,
  DataTableProvider,
  type TableColumnDef,
} from "../../components/data-table";
import { Badge } from "../../components/ui/badge";

interface ApplicationStats {
  total: number;
  approved: number;
  pending: number;
  rejected: number;
}

interface Application {
  id: string;
  status: string;
  applied_date: string;
  interview_date: string | null;
  hostel: {
    name: string;
    city: string | null;
  } | null;
}

interface StudentProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  mobile: string | null;
}

export default function StudentDashboard() {
  const { user, userRole, loading } = useAuth();
  const navigate = usePrefixedNavigate();
  const [stats, setStats] = useState<ApplicationStats>({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
  });
  const [applications, setApplications] = useState<Application[]>([]);
  const [studentProfile, setStudentProfile] = useState<StudentProfile | null>(
    null
  );
  const [, setProfileLoading] = useState(true);

  useEffect(() => {
    if (
      !loading &&
      userRole &&
      userRole !== "student" &&
      userRole !== "parent"
    ) {
      navigate("/");
    }
  }, [userRole, loading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        return;
      }

      try {
        // Fetch student profile
        const { data: studentData } = await supabase
          .from("students")
          .select("id, first_name, last_name, email, mobile")
          .eq("user_id", user.id)
          .maybeSingle();

        if (studentData) {
          setStudentProfile(studentData);

          // Fetch applications with hostel info
          const { data: appData } = await supabase
            .from("applications")
            .select(
              `
              id, status, applied_date, interview_date,
              hostel_id
            `
            )
            .eq("student_id", studentData.id)
            .order("applied_date", { ascending: false });

          if (appData) {
            // Fetch hostel names separately
            const hostelIds = appData
              .map((a: any) => a.hostel_id)
              .filter(Boolean);
            let hostelsMap: Record<
              string,
              { name: string; city: string | null }
            > = {};

            if (hostelIds.length > 0) {
              const { data: hostels } = await supabase
                .from("hostels")
                .select("id, name, city")
                .in("id", hostelIds as string[]);

              if (hostels) {
                hostelsMap = hostels.reduce(
                  (acc, h) => {
                    acc[h.id] = { name: h.name, city: h.city };
                    return acc;
                  },
                  {} as Record<string, { name: string; city: string | null }>
                );
              }
            }

            const applicationsWithHostels = appData.map(a => ({
              ...a,
              hostel: a.hostel_id ? hostelsMap[a.hostel_id] || null : null,
            }));

            setApplications(applicationsWithHostels as Application[]);
            setStats({
              total: appData.length,
              approved: appData.filter(a => a.status === "approved").length,
              pending: appData.filter(a => a.status === "pending").length,
              rejected: appData.filter(a => a.status === "rejected").length,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setProfileLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const upcomingInterviews = applications.filter(
    a => a.interview_date && new Date(a.interview_date) > new Date()
  );

  const interviewTableColumns = useMemo<TableColumnDef<Application>[]>(
    () => [
      {
        id: "hostel",
        label: "Hostel Name",
        width: 180,
        renderCell: app => (
          <span className="font-medium">{app.hostel?.name ?? "N/A"}</span>
        ),
      },
      {
        id: "date",
        label: "Interview Date",
        width: 140,
        renderCell: app => formatDate(app.interview_date),
      },
      {
        id: "status",
        label: "Status",
        width: 120,
        renderCell: () => <Badge variant="secondary">Scheduled</Badge>,
      },
    ],
    []
  );

  const applicationsTableColumns = useMemo<TableColumnDef<Application>[]>(
    () => [
      {
        id: "hostel",
        label: "Hostel",
        width: 160,
        renderCell: app => (
          <span className="font-medium">{app.hostel?.name ?? "N/A"}</span>
        ),
      },
      {
        id: "city",
        label: "City",
        width: 120,
        renderCell: app => app.hostel?.city ?? "N/A",
      },
      {
        id: "appliedDate",
        label: "Applied Date",
        width: 120,
        renderCell: app => formatDate(app.applied_date),
      },
      {
        id: "status",
        label: "Status",
        width: 120,
        renderCell: app => (
          <Badge
            variant="secondary"
            className={
              app.status === "approved"
                ? "bg-green-600 text-white hover:bg-green-700"
                : app.status === "rejected"
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : app.status === "pending"
                    ? "bg-orange-600 text-white hover:bg-orange-700"
                    : ""
            }
          >
            {app.status}
          </Badge>
        ),
      },
    ],
    []
  );

  return (
    <StudentLayout>
      <div className="space-y-6">
        <div>
          <h2 className="mb-2 text-3xl font-bold text-foreground">
            Student Dashboard
          </h2>
          <p className="text-muted-foreground">
            {studentProfile
              ? `Welcome, ${studentProfile.first_name}!`
              : "Track your applications and discover hostels"}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="border-[#8B0000]/30 bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Total Applications
              </CardTitle>
              <FileText className="h-4 w-4 text-[#8B0000]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#8B0000]">
                {stats.total}
              </div>
              <p className="text-xs text-muted-foreground">All applications</p>
            </CardContent>
          </Card>

          <Card className="border-green-500/30 bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Approved</CardTitle>
              <Users className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.approved}
              </div>
              <p className="text-xs text-muted-foreground">
                Successful applications
              </p>
            </CardContent>
          </Card>

          <Card className="border-orange-500/30 bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <FileText className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {stats.pending}
              </div>
              <p className="text-xs text-muted-foreground">Awaiting review</p>
            </CardContent>
          </Card>

          <Card className="border-red-500/30 bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Rejected</CardTitle>
              <FileText className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {stats.rejected}
              </div>
              <p className="text-xs text-muted-foreground">
                Unsuccessful applications
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Interviews */}
        {upcomingInterviews.length > 0 && (
          <DataTableProvider
            tableId="student-dashboard-interviews"
            columns={interviewTableColumns}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                <CardTitle>Upcoming Interviews</CardTitle>
                <DataTableColumnSettings columns={interviewTableColumns} />
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <DataTable
                  embedded
                  hideToolbar
                  tableId="student-dashboard-interviews"
                  columns={interviewTableColumns}
                  data={upcomingInterviews}
                  getRowKey={app => app.id}
                />
              </CardContent>
            </Card>
          </DataTableProvider>
        )}

        {/* Recent Applications */}
        <DataTableProvider
          tableId="student-dashboard-applications"
          columns={applicationsTableColumns}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
              <CardTitle>Recent Applications</CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/applications")}
                >
                  View All
                </Button>
                <DataTableColumnSettings columns={applicationsTableColumns} />
              </div>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              {applications.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="mb-4 text-muted-foreground">
                    No applications yet
                  </p>
                  <Button
                    onClick={() => navigate("/apply")}
                    className="bg-[#8B0000] text-white hover:bg-[#6B0000]"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Apply to Hostel
                  </Button>
                </div>
              ) : (
                <DataTable
                  embedded
                  hideToolbar
                  tableId="student-dashboard-applications"
                  columns={applicationsTableColumns}
                  data={applications.slice(0, 5)}
                  getRowKey={app => app.id}
                />
              )}
            </CardContent>
          </Card>
        </DataTableProvider>

        {/* Quick Actions */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                className="w-full bg-[#8B0000] text-white hover:bg-[#6B0000]"
                onClick={() => navigate("/apply")}
              >
                <Plus className="mr-2 h-4 w-4" /> Apply to New Hostel
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/scholarships")}
              >
                <Award className="mr-2 h-4 w-4" /> Browse Scholarships
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Need Help?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-sm text-muted-foreground">
                Contact our support team for assistance with your applications
                or any queries.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/help")}
              >
                <MessageSquare className="mr-2 h-4 w-4" /> Contact Support
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </StudentLayout>
  );
}
